import { Card, Group, SimpleGrid, Text, Title, Tooltip } from "@mantine/core";
import {
  IconCalendarWeek,
  IconCircleCheck,
  IconInfoCircle
} from "@tabler/icons-react";
import { DateTime } from "luxon";
import { Link } from "react-router";
import { TodoStatus, type Todo } from "../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { NoDataMessage } from "../../BuildingBlocks/NoDataMessage/NoDataMessage";
import "./TodosDueSoon.scss";

const now = DateTime.now();

interface TodosDueSoonProps {
  todos: Array<Todo>;
}

function TodosDueSoon({ todos }: TodosDueSoonProps) {
  const dueSoonTodos = todos
    .filter((todo) => todo.status === TodoStatus.Open)
    .filter((todo) => {
      if (todo.dueDate) {
        // Mieterstrom defined "soon" as "within 2 months"
        const dueDate = backendDateOrDateTimeToLuxonDateTimeOrNull(
          todo.dueDate
        );
        return dueDate && now < dueDate && dueDate < now.plus({ months: 2 });
      }
      return false;
    })
    .sort((todoA, todoB) => {
      // due dates are never null, but Typescript doesn't know that
      const dueDateA = todoA.dueDate ? new Date(todoA.dueDate).valueOf() : 0;
      const dueDateB = todoB.dueDate ? new Date(todoB.dueDate).valueOf() : 0;
      return dueDateA - dueDateB;
    });

  return (
    <Card className="TodosDueSoon" p="lg">
      <Group className="header" justify="space-between" mb="xl">
        <Title order={5}>Bald fällig</Title>
        <Tooltip label="Diese Aufgaben sind in den nächsten zwei Monaten fällig.">
          <IconInfoCircle
            color="var(--mantine-primary-color-filled)"
            size="1.3rem"
          />
        </Tooltip>
      </Group>
      {dueSoonTodos.length > 0 ? (
        <SimpleGrid cols={2} spacing="lg">
          {dueSoonTodos.map((todo) => (
            <Group className="due-soon-element" key={todo.id} wrap="nowrap">
              <IconCalendarWeek className="calendar-icon" size="1.2em" />
              <Text>{todo.dueDate}</Text>
              <Link className="label-with-link" to={`./${todo.id}`}>
                {todo.label}
              </Link>
            </Group>
          ))}
        </SimpleGrid>
      ) : (
        <Group className="no-due-soon" gap="xs" justify="center" mt="lg">
          <IconCircleCheck
            color="var(--mantine-primary-color-filled)"
            size="1.4rem"
          />
          <NoDataMessage message="Keine dringenden Aufgaben" p={0} />
        </Group>
      )}
    </Card>
  );
}

export { TodosDueSoon };
